import validator from "validator";
import hex2rgba from 'hex-to-rgba';
import CryptoJS from 'crypto-js';

export const throttle = (callback, time) => {
    let isWaiting = false; // 클로저 내부에 isWaiting 저장

    return (...args) => {
        if (isWaiting) {
            return;
        }

        isWaiting = true;
        callback.apply(this, args);

        setTimeout(() => {
            isWaiting = false;
        }, time);
    };
};

export const debounce = (callback, time) => {
    let timer;
    return function () {
        clearTimeout(timer);
        timer = setTimeout(_ => callback.apply(this, arguments), time);
    };
};

export const merge = (obj = {}, defaults) => {
    for (const key in defaults) {
        if (typeof obj[key] === 'undefined') {
            obj[key] = defaults[key];
        }
    }
    return obj;
}

export const isByteLength = (str, options) => {
    assertString(str);
    let min;
    let max;
    if (typeof (options) === 'object') {
        min = options.min || 0;
        max = options.max;
    } else { // backwards compatibility: isByteLength(str, min [, max])
        min = arguments[1];
        max = arguments[2];
    }
    const len = encodeURI(str).split(/%..|./).length - 1;
    return len >= min && (typeof max === 'undefined' || len <= max);
}

export const assertString = (input) => {
    const is_string = typeof input === 'string' || input instanceof String;
    if (!is_string) {
        let invalid_type = typeof input;
        if (input === null) {
            invalid_type = 'null'
        } else if (invalid_type === 'object') {
            invalid_type = input.constructor.name
        }

        console.error("[Util][assertString]", {input, invalid_type});
    }
}

export const stringFormat = (...args) => {
    return args[0].replace(/{(\d+)}/g, (match, num) => {
        num = Number(num) + 1;
        return typeof (args[num]) !== undefined ? args[num] : match;
    });
};

export const stringToNumber = (string) => {
    try {
        return Number(string);
    } catch (e) {
        return 0;
    }
};

export const numberWithCommas = (number) => {
    return stringToNumber(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const rgba = (hexColor = "", opacity = 1) => {
    return hex2rgba(hexColor, opacity);
};

export const hexToRgba = (hex = "", opacity, backgroundHex = '#FFFFFF') => {
    if (!hex) {
        return "";
    }

    // 입력 검증hexToRgba
    if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
        throw new Error("유효하지 않은 hex 색상 코드입니다.");
    }
    if (opacity < 0 || opacity > 1) {
        throw new Error("opacity는 0과 1 사이여야 합니다.");
    }

    // 3자리 hex를 6자리로 변환
    function expandShortHex(shortHex) {
        return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
    }

    if (hex.length === 4) hex = expandShortHex(hex);
    if (backgroundHex.length === 4) backgroundHex = expandShortHex(backgroundHex);

    // hex 색상을 RGB로 변환
    function hexToRgb(hex) {
        const bigint = parseInt(hex.slice(1), 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255,
        };
    }

    const foregroundRgb = hexToRgb(hex);
    const backgroundRgb = hexToRgb(backgroundHex);

    // alpha 혼합 계산
    const blendedRgb = {
        r: Math.round(foregroundRgb.r * opacity + backgroundRgb.r * (1 - opacity)),
        g: Math.round(foregroundRgb.g * opacity + backgroundRgb.g * (1 - opacity)),
        b: Math.round(foregroundRgb.b * opacity + backgroundRgb.b * (1 - opacity)),
    };

    // RGB를 6자리 hex로 변환
    function rgbToHex({r, g, b}) {
        return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    }

    return rgbToHex(blendedRgb);
}


export const normalizeSearchText = (value) => {
    if (!value) {
        return "";
    }
    return value.normalize().replace(/ /gi, "").toLowerCase()
};
export const deepIsEqual = (first, second) => {
    if (first === second) {
        return true;
    }

    if (typeof first !== typeof second) {
        return false;
    } else {
        if (typeof first !== 'object' && typeof second !== 'object') {
            if (first !== second) {
                return false;
            }

        } else if (first === null || second === null || first === undefined || second === undefined) {
            if (first !== second) {
                return false;
            }
        }
    }

    let firstProps = Object.getOwnPropertyNames(first);
    let secondProps = Object.getOwnPropertyNames(second);

    if (firstProps.length !== secondProps.length) return false;

    for (let i = 0; i < firstProps.length; i++) {
        let prop = firstProps[i];

        if (first[prop] instanceof Node) {
            if (first[prop] !== second[prop]) return false;
        } else {
            switch (typeof (first[prop])) {
                case 'object':
                    if (!deepIsEqual(first[prop], second[prop])) return false;
                    break;
                case 'number':
                    if (isNaN(first[prop]) && isNaN(second[prop])) break;
                    break;
                default:
                    if (first[prop] !== second[prop]) return false;
            }
        }
    }

    return true;
};

// 타입 체크 관련
export const isObjectOnly = (input) => {
    return typeof input === 'object' && input !== null && !Array.isArray(input);
};


// 로컬스토리지 관련
export const setLocalStorage = (key, data) => {
    if (!window.localStorage) {
        return;
    }

    window.localStorage.setItem(key, JSON.stringify(data))
};
export const getLocalStorage = (key) => {
    if (!window.localStorage) {
        return;
    }

    const data = window.localStorage.getItem(key);
    if (data) {
        return JSON.parse(data)
    }
};
export const removeLocalStorage = (key) => {
    if (!window.localStorage) {
        return;
    }
    window.localStorage.removeItem(key);
};
const scrollHandler = (e) => {
    console.log('scroll');
};

const throttledHandler = throttle(scrollHandler, 500); // throttle이 반환하는 함수


// 서비스 관련
export const getAvatarText = (name) => {
    let text = "";
    if (name) {
        if (isAlphanumeric(name.substring(0, 1))) {
            text = name.substring(0, 1);
        } else if (name.length > 3) {
            text = name.substring(0, 1);
        } else if (name.length > 1) {
            text = name.substring(name.length - 2, name.length);
        } else {
            text = name.substring(name.length - 1, name.length);
        }
    }

    return text;
};

export const onlyDigits = (s) => {
    return (s || '').replace(/\D/g, '');
};

export const isRRNValidDateYYMMDD = (yy, mm, dd) => {
    const y = parseInt(yy, 10);
    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);
    if (m < 1 || m > 12) return false;
    const isLeap = (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
    const days = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
    return d >= 1 && d <= days;
};

export const formatPhone = (value) => {
    let newValue = removeNumberOtherText(value || '');
    if (!isNumeric(newValue)) {
        return value;
    }

    let phoneNumberSplit = []
        , valueLength = newValue.length;

    if (valueLength === 11) {
        phoneNumberSplit = newValue.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3").split(' ');
    } else if (valueLength === 10) {
        phoneNumberSplit = newValue.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3").split(' ');
    }

    if (phoneNumberSplit.length > 0) {
        return phoneNumberSplit.join("-");
    }

    return value;
};

export const formatRRN = (input) => {
    const digits = onlyDigits(input);
    return digits.length <= 11 ? formatRRN11(digits) : formatRRN13(digits);
};

export const formatRRN11 = (input) => {
    const d = onlyDigits(input).slice(0, 11);
    return d.length <= 6 ? d : `${d.slice(0, 6)}-${d.slice(6)}`;
};

export const formatRRN13 = (input) => {
    const d = onlyDigits(input).slice(0, 13);
    return d.length <= 6 ? d : `${d.slice(0, 6)}-${d.slice(6)}`;
};

export function formatBRN(input) {
    const d = onlyDigits(input);
    return d.length <= 10 ? formatBRN10(d) : formatCRN13(d);
}

export const formatBRN10 = (input) => {
    const d = onlyDigits(input).slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export const formatCRN13 = (input) => {
    const d = onlyDigits(input).slice(0, 13);
    return d.length <= 6 ? d : `${d.slice(0, 6)}-${d.slice(6)}`;
};

export const removeNumberOtherText = (value) => {
    return (value || '').replace(/[^0-9]/g, "");
}

// 유효성 검사
export const isNumeric = (value) => {
    return validator['isNumeric'](value);
};

export const isAlphanumeric = (value) => {
    return validator['isAlphanumeric'](value);
};

export const isRRN = (input) => {
    const digits = onlyDigits(input);
    if (digits.length === 11) {
        return isRRN11(digits);
    }
    if (digits.length === 13) {
        return isRRN13(digits);
    }
    return false; // {ok: false, reason: 'length', expected: '11|13'};
}

export const isRRN11 = (input) => {
    const d = onlyDigits(input);
    if (d.length !== 11) {
        return false; // { ok: false, reason: 'length' };
    }
    const [yy, mm, dd, s] = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6), d[6]];
    if (!isRRNValidDateYYMMDD(yy, mm, dd)) {
        return false; // { ok: false, reason: 'date' };
    }
    if (!/[1-8]/.test(s)) {
        return false; //  { ok: false, reason: 'serial' };
    }
    return true; //{ok: true, reason: 'no-checksum'}; // 체크섬 불가
};

export const isRRN13 = (input) => {
    const d = onlyDigits(input);
    if (d.length !== 13) {
        return false; //  {ok: false, reason: 'length'};
    }
    const [yy, mm, dd, s] = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6), d[6]];
    if (!isRRNValidDateYYMMDD(yy, mm, dd)) {
        return false; // {ok: false, reason: 'date'};
    }
    if (!/[1-8]/.test(s)) { // 1~4 내국인, 5~8 외국인
        return false; // {ok: false, reason: 'serial'};
    }
    const weights = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5];
    const sum = weights.reduce((acc, w, i) => acc + w * parseInt(d[i], 10), 0);
    const check = (11 - (sum % 11)) % 10;
    return check === parseInt(d[12], 10);
};

export const isBRN = (input) => {
    const d = onlyDigits(input);
    if (d.length === 10) {
        return isBRN10(d);
    }
    if (d.length === 13) {
        return isCRN13(d);
    }
    return false; //{ok: false, reason: 'length', expected: '10|13'};
};

export const isBRN10 = (input) => {
    const d = onlyDigits(input);
    if (d.length !== 10) {
        return false; // { ok: false, reason: 'length' };
    }
    const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        const prod = parseInt(d[i], 10) * w[i];
        sum += prod;
        if (i === 8) {
            sum += Math.floor(prod / 10); // 9번째 가중치(5)는 십의 자리 추가
        }
    }
    const check = (10 - (sum % 10)) % 10;
    return check === parseInt(d[9], 10);
};

export const isCRN13 = (input) => {
    const d = onlyDigits(input);
    if (d.length !== 13) {
        return false; // {ok: false, reason: 'length'};
    }

    const w = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2];
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const prod = parseInt(d[i], 10) * w[i];
        sum += Math.floor(prod / 10) + (prod % 10); // 곱한 수의 각 자리 합
    }
    const check = (10 - (sum % 10)) % 10;
    return check === parseInt(d[12], 10);
};


export const isURL = (url, option = {}) => {
    /*
    https://www.npmjs.com/package/validator
    option
        - require_protocol - if set to true isURL will return false if protocol is not present in the URL.
        - require_valid_protocol - isURL will check if the URL's protocol is present in the protocols option.
        - protocols - valid protocols can be modified with this option.
        - require_host - if set to false isURL will not check if host is present in the URL.
        - require_port - if set to true isURL will check if port is present in the URL.
        - allow_protocol_relative_urls - if set to true protocol relative URLs will be allowed.
        - allow_fragments - if set to false isURL will return false if fragments are present.
        - allow_query_components - if set to false isURL will return false if query components are present.
        - validate_length - if set to false isURL will skip string length validation (2083 characters is IE max URL length).
    * */

    console.log(url, option, validator["isURL"](url, option))
    return validator["isURL"](url, option);
};

export const isRequire = (requiredKeys, data) => {
    if (!requiredKeys) {
        return;
    }

    const requiredKeyList = Object.keys(requiredKeys);
    if (!requiredKeyList || requiredKeyList.length === 0) {
        return;
    }

    const error = {};
    requiredKeyList.forEach((requiredKey) => {
        if (requiredKeys[requiredKey] === true && !data[requiredKey]) {
            error[requiredKey] = "invalid";
        }
    })
    return error;
};

export const isEmail = (value) => {
    return validator['isEmail'](value);
};

export const isPhone = (value) => {
    return /^(01[0-9])([0-9]{3,4})([0-9]{4})$/.test(value);
};

export const isCellPhone = (value) => {
    return /^(01[0-9])([0-9]{3,4})([0-9]{4})$/.test(value);
};

export const getSignImgSize = (imgW, imgH, option) => {
    if (!option) throw new Error("option is required");

    const boxW = option.width;
    const boxH = option.height;

    const maxW = option.maxWidth ?? boxW;
    const maxH = option.maxHeight ?? boxH;

    // 이미지 비율
    const imageRatio = imgW / imgH;

    // 우선 원본 비율로 box에 fit한 값 계산
    let scaledW = boxW;
    let scaledH = scaledW / imageRatio;

    if (scaledH > boxH) {
        scaledH = boxH;
        scaledW = scaledH * imageRatio;
    }

    // maxWidth / maxHeight 적용
    if (scaledW > maxW || scaledH > maxH) {
        const widthRatio = maxW / scaledW;
        const heightRatio = maxH / scaledH;
        const scaleRatio = Math.min(widthRatio, heightRatio);

        scaledW *= scaleRatio;
        scaledH *= scaleRatio;
    }

    return {
        width: Math.round(scaledW),
        height: Math.round(scaledH)
    };
};

export const text2enc = (encType, secretKey, plainText) => {
    if (plainText && secretKey) {
        return CryptoJS.AES.encrypt(plainText, secretKey).toString();
    }

    return plainText;
};

export const dec2text = (encType, secretKey, encText) => {
    const bytes = CryptoJS.AES.decrypt(encText, secretKey);
    return bytes.toString(CryptoJS.enc.Utf8);
};


export const getExtName = (filePath) => {
    // 파일 경로에서 마지막 '.' 이후의 문자열을 추출합니다.
    const baseName = filePath.split('/').pop(); // 파일 경로에서 파일명만 분리
    const lastDotIndex = baseName.lastIndexOf('.');

    // '.'이 없으면 빈 문자열 반환
    if (lastDotIndex === -1) return '';

    // '.' 이후의 문자열(확장자) 반환
    return baseName.slice(lastDotIndex + 1).toUpperCase();
};


export const toCamelCase = (snakeCaseStr) => {
    return snakeCaseStr
        .toLowerCase()
        .split('_')
        .map((word, index) =>
            index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join('');
};

export const toSnakeUpperCase = (camelCaseStr) => {
    return camelCaseStr
        .replace(/([A-Z])/g, '_$1') // 대문자 앞에 언더스코어 추가
        .toUpperCase();
};


